const TAILWIND_COLOR_TO_HEX = {
  "bg-green-400": "#34D399",
  "bg-red-400": "#F87171",
  "bg-blue-400": "#60A5FA",
  "bg-yellow-400": "#FBBF24",
  "bg-orange-400": "#FB923C",
  "bg-purple-400": "#C084FC",
  "bg-pink-400": "#F472B6",
  "bg-indigo-400": "#818CF8",
  "bg-gray-400": "#9CA3AF",
  "bg-slate-400": "#94A3B8",
  "bg-cyan-400": "#22D3EE",
  "bg-teal-400": "#2DD4BF",
  "bg-lime-400": "#A3E635",
  "bg-amber-400": "#FBBF24",
  "bg-rose-400": "#FB7185",
  "bg-black": "#020617",
  "bg-white": "#FFFFFF",
};

const DEFAULT_CHART_COLORS = [
  "#2563EB",
  "#F97316",
  "#16A34A",
  "#9333EA",
  "#E11D48",
  "#0891B2",
  "#CA8A04",
  "#4F46E5",
];

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (userInfo) => Number(userInfo?.role_id) === 4;

export const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(toNumber(value));

export const formatDate = (value, options = {}) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  });
};

export const formatShortDate = (value) =>
  formatDate(value, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

export const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getInitials = (firstname = "", lastname = "") => {
  const first = firstname?.trim()?.[0] || "";
  const last = lastname?.trim()?.[0] || "";
  const initials = `${first}${last}`.toUpperCase();

  return initials || "AD";
};

export const getAdherentDisplayName = (adherent) => {
  const nom = adherent?.nom || adherent?.last_name || "";
  const prenom = adherent?.prenom || adherent?.first_name || "";

  return `${prenom} ${nom}`.trim() || "Adhérent";
};

export const getAdherentCreatedDate = (adherent) =>
  adherent?.createdAt || adherent?.created_at || adherent?.date_creation;

export const getRecentAdherents = (adherents = [], limit = 8) => {
  return [...adherents]
    .sort((a, b) => {
      const dateA = new Date(getAdherentCreatedDate(a)).getTime() || 0;
      const dateB = new Date(getAdherentCreatedDate(b)).getTime() || 0;

      return dateB - dateA;
    })
    .slice(0, limit);
};

export const getGroupColorMap = (groups = []) => {
  return groups.reduce((acc, group, index) => {
    const groupName = group?.nom || group?.name || group?.adherent_groupe;

    if (!groupName) return acc;

    const tailwindColor = group?.color_groupe;
    const hexColor =
      TAILWIND_COLOR_TO_HEX[tailwindColor] ||
      DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];

    acc[groupName] = {
      className: tailwindColor || "bg-slate-400",
      hex: hexColor,
    };

    return acc;
  }, {});
};

export const normalizeGroupDistributionItem = (item, index, groupColorMap) => {
  const name =
    item?.adherent_groupe ||
    item?.groupe ||
    item?.group ||
    item?.nom ||
    `Groupe ${index + 1}`;

  const value = toNumber(item?.count || item?.total || item?.value);
  const color =
    groupColorMap?.[name]?.hex ||
    DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];

  return {
    name,
    value,
    color,
  };
};

export const buildGroupChartData = (
  groupDistribution = [],
  groupColorMap = {}
) => {
  return groupDistribution
    .map((item, index) =>
      normalizeGroupDistributionItem(item, index, groupColorMap)
    )
    .filter((item) => item.value > 0);
};

export const getGenderLabel = (value) => {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized === "M") return "Masculin";
  if (normalized === "F") return "Féminin";

  return value || "Non renseigné";
};

export const buildGenderChartData = (genderDistribution = []) => {
  return genderDistribution
    .map((item, index) => {
      const genre = item?.genre || item?.sexe || item?.gender || "Autre";

      return {
        name: getGenderLabel(genre),
        value: toNumber(item?.count || item?.total || item?.value),
        color:
          genre === "M"
            ? "#2563EB"
            : genre === "F"
            ? "#E11D48"
            : DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
      };
    })
    .filter((item) => item.value > 0);
};

export const getTotalFromDistribution = (items = []) =>
  items.reduce(
    (total, item) => total + toNumber(item?.count || item?.total || item?.value),
    0
  );

export const getTotalVisits = (visits = []) =>
  visits.reduce((total, item) => total + toNumber(item?.count), 0);

export const getDashboardStats = ({
  adherents = [],
  groupDistribution = [],
  genderDistribution = [],
  visits = [],
  messages = [],
  notifications = [],
}) => {
  const totalAdherents =
    adherents.length || getTotalFromDistribution(groupDistribution);

  return {
    totalAdherents,
    totalGroups: groupDistribution.length,
    totalGenderItems: genderDistribution.length,
    totalVisits: getTotalVisits(visits),
    totalMessages: messages.length,
    totalNotifications: notifications.length,
    unreadMessages: messages.filter((message) => !message.read_message).length,
    unreadNotifications: notifications.filter(
      (notification) => !notification.read_notification
    ).length,
  };
};

export const getMessageDate = (message) =>
  message?.created_at || message?.createdAt || message?.date_creation;

export const getNotificationDate = (notification) =>
  notification?.created_at ||
  notification?.createdAt ||
  notification?.date_creation;

export const getVisitDateLabel = (item) =>
  item?.date_visite || item?.date || item?.created_at || "";

export const getReadButtonLabel = (isRead) =>
  isRead ? "Marquer comme non lu" : "Marquer comme lu";